/**
 * The client half of the session lifecycle (§7.11, D47, §13 "reuses its session record rather than consuming an additional slot").
 *
 * **One object owns the whole of it** — the per-tab access token, the durable
 * refresh/binding pair, the boot-time silent restore, the single-flight refresh on `401`,
 * and every teardown path — because the property that matters is negative: *no request
 * leaves this app without the binding header, and no `401` is retried more than once before
 * the session is torn down.* A second component that "just calls fetch with the token" is
 * what breaks that, and nothing but review discipline would catch it, so there is exactly
 * one door.
 *
 * **What this module is not.** It decides nothing. `contracts.md` §10's rule for the access
 * mirror holds for the session too: cached client state is never evidence. A restored
 * session is a *claim* that the next request either confirms or refuses — the middleware in
 * `packages/functions/auth/middleware.ts` is the authority, and every path below treats its
 * `401` as final rather than as something to work around.
 *
 * **The access token is per-tab, in `sessionStorage` (D152).** It was memory-only before —
 * `device-cache.md` → *"Access token and in-flight request state"* — which meant a reload
 * always began with a refresh, and repeated Refresh rotated the refresh family fast enough to
 * trip the server's single-use replay detection and sign the tab out. D152 promotes that 🔸
 * assumption on the record that a same-tab SPA's exposure window barely widens (tab lifetime
 * vs. page lifetime) for a real fix: `start()` adopts a still-unexpired stored token and skips
 * `/auth/refresh` altogether. A durable refresh marker (`credentials.ts`) and a best-effort
 * Web Lock around the refresh call cover the narrower window that remains — a refresh actually
 * in flight when the reload lands — without ever suppressing a genuine `401`.
 */

import { ROUTES, buildPath } from '@selogate/core'
import {
  clearAccessToken,
  clearCredentials,
  clearRefreshMarker,
  clearResumeHint,
  readAccessToken,
  readCredentials,
  readRefreshMarker,
  readResumeHint,
  writeAccessToken,
  writeCredentials,
  writeRefreshMarker,
  writeResumeHint,
  type StoredCredentials,
} from './credentials'
import { ACCESS_TOKEN_SKEW_SECONDS, CLIENT_BUILD_HEADER, SESSION_BINDING_HEADER, STAGE } from './config'
import { BUILD } from '../pwa/version'
import {
  NetworkError,
  SessionUnavailableError,
  field,
  numberField,
  stringField,
  type ApiRequest,
  type ApiResponse,
  type ApiTransport,
} from './transport'
import { hasOtherAccountRows, purgeAccount, purgeOtherAccounts } from '../device-cache/purge'
import { readProfile, writeProfile, type CachedProfile } from '../device-cache/profile'
import {
  StoreConnectionLostError,
  errorNameOf,
  openDeviceStore,
  reopenDeviceStore,
  type KeyValueStore,
} from '../device-cache/store'
import { recoverCredentialWrite, recoverLostStoreConnection } from './storeRecovery'
import { LegacyCredentialsUnreadableError } from './nativeCredentials'
import { reportCredentialReadFailed, reportRefreshRefused, reportSignInPurgeFailed } from '../telemetry/sink'

import { isLocale, type Locale } from '../i18n/locales'

/**
 * Re-exported so the many `auth`-facing call sites keep importing it from here. **The definition
 * moved to `i18n/locales.ts` (D200)** — it had been spelled out twice, and a locale added to the
 * catalogs but not to this copy typechecks everywhere and then narrows to Hebrew at runtime.
 */
export type { Locale }

/**
 * Why a session ended. All four clear the same state; they differ only in what the UI says,
 * and `revoked` is the one the user did not ask for.
 */
export type SessionEndReason = 'sign-out' | 'revoked' | 'storage-lost'

export interface SessionState {
  /**
   * **`restoring` is not `signed-out`, and the difference is the whole of the 2026-09-25 fix.**
   * It means *the boot read of the credentials rejected, and the manager is reading again* — the
   * row may well be on disk. The router treats it as undecided (never the OTP screen) and
   * `request()` waits for it. Only a read that **succeeds with no row** is `signed-out`.
   */
  readonly state: 'loading' | 'restoring' | 'signed-out' | 'signed-in'
  readonly userId?: string
  readonly sessionId?: string
  /** D42 — absent until captured. `pendingName` in the router's `AuthStatus` derives from it. */
  readonly name?: string
  readonly locale?: Locale
  /**
   * Whether the cloud is currently reachable. **`false` is a read-only app**, not an error:
   * *"the app still opens to Home from cache, and every cloud action is unavailable rather
   * than optimistic."*
   */
  readonly online: boolean
  /** `false` when the browser gave us no durable storage — this session ends with the tab. */
  readonly durableStorage: boolean
  /** Set once when a session ended without the user asking. Cleared by the next sign-in. */
  readonly endedReason?: SessionEndReason
  /**
   * **`signed-out` because the phone's storage could not be read, NOT because it was empty.** Set
   * only when {@link CREDENTIAL_READ_RETRY_BUDGET_MS} ran out with every read rejecting; the sign-in
   * screen reads it to say so and to offer `retryRestore()`. The row may still be on disk, so this
   * is never a purge and never `endedReason`. Cleared by a sign-in or a retry.
   */
  readonly credentialReadFailed?: boolean | undefined
}

const INITIAL: SessionState = { state: 'loading', online: true, durableStorage: true }
const CONNECTIVITY_RETRY_DELAYS_MS = [0, 1_000, 3_000] as const

/**
 * 🔸 **Unmeasured — the backoff for re-reading credentials a boot could not read.** The first
 * re-read waits this long, and each later one doubles up to {@link CREDENTIAL_READ_RETRY_MAX_DELAY_MS}.
 * Author numbers with no distribution behind them: the store's own 2 s transaction budget is itself
 * 🔸 (`STORE_TRANSACTION_TIMEOUT_MS`, D305), so nothing yet says how long a real phone's IndexedDB
 * stays unreadable after a cold open. `credential-read-failed`'s `attempt` field is the instrument.
 */
export const CREDENTIAL_READ_RETRY_FIRST_DELAY_MS = 250
/** 🔸 Unmeasured — the ceiling the doubling above stops at. */
export const CREDENTIAL_READ_RETRY_MAX_DELAY_MS = 2_000
/**
 * 🔸 **Unmeasured — how long a boot keeps re-reading before it shows the sign-in screen, and the
 * bound R1-3 requires.** No attempt STARTS past it; the attempt already running is bounded by the
 * store's own deadlines (`STORE_TRANSACTION_TIMEOUT_MS`, and `STORE_OPEN_TIMEOUT_MS` when a lost
 * connection is reopened), so the worst case is this plus one of those, never unbounded. A store
 * that rejects forever ends here, on the sign-in screen with the storage line and Try again.
 */
export const CREDENTIAL_READ_RETRY_BUDGET_MS = 15_000

/** What one restore run concluded. `failed` is the budget running out, never an empty store. */
type RestoreOutcome = 'signed-in' | 'absent' | 'failed'

/**
 * A reopen inside a restore run handed back a memory store. Thrown so the attempt counts as
 * **failed** — a memory store answers every read with nothing, which must never read as *no row*.
 * Its `name` is what the sink reports for it, so it stays inside the server's token pattern.
 */
class StoreDegradedToMemoryError extends Error {
  constructor() {
    super('store-degraded-to-memory')
    this.name = 'DegradedToMemory'
  }
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Whether a patch would actually move `SessionState`.
 *
 * **Per-key `===` is total here, and that is a property of the type rather than an
 * approximation.** `SessionState` is flat primitives only — two string unions, three optional
 * strings, one optional locale, two booleans — with no nested object and no array, so a shallow
 * compare is a deep compare. **A non-primitive field added above breaks that silently**, since
 * a new object literal per patch would compare unequal every time and quietly restore the
 * every-response notify this guard exists to remove.
 *
 * A key the patch carries with `undefined` against a key the state does not carry at all
 * compares equal, which is the right answer: nothing a reader can observe differs.
 */
function patchMovesState(state: SessionState, patch: Partial<SessionState>): boolean {
  for (const key of Object.keys(patch) as (keyof SessionState)[]) {
    if (patch[key] !== state[key]) return true
  }
  return false
}

/** The same comparison over two whole states — a replacement may *remove* a key, so both sides' keys count. */
function statesDiffer(a: SessionState, b: SessionState): boolean {
  const keys = new Set<keyof SessionState>([
    ...(Object.keys(a) as (keyof SessionState)[]),
    ...(Object.keys(b) as (keyof SessionState)[]),
  ])
  for (const key of keys) if (a[key] !== b[key]) return true
  return false
}

export interface VerifiedSignIn {
  readonly credentials: StoredCredentials
  readonly profile: CachedProfile
}

/**
 * Why the last refresh returned `false`. It exists so `request()` can throw the *honest*
 * error type: only `offline` is the phone having no reception, and only that one may be
 * rendered as "check your signal".
 */
type RefreshFailure = 'offline' | 'server' | 'ended'

/**
 * D152 — narrowed to the one method used, so a test can inject a fake queue without a real
 * `navigator.locks` (jsdom has none). Exclusive by default, which is all `LockManager.request`
 * needs to ask for.
 */
type LockRequest = (name: string, fn: () => Promise<boolean>) => Promise<boolean>

function defaultLocks(): LockRequest | undefined {
  const locks = globalThis.navigator?.locks
  if (locks === undefined) return undefined
  return (name, fn) => locks.request(name, fn)
}

const refreshLockName = (stage: string): string => `selogate-refresh-${stage}`

export class SessionManager {
  #state: SessionState = INITIAL
  #listeners = new Set<(state: SessionState) => void>()
  #accessToken: string | undefined
  #accessTokenExpiresAt = 0
  #credentials: StoredCredentials | undefined
  #refreshing: Promise<boolean> | undefined
  /**
   * Set inside `#doRefresh`, never inside `#refresh` — the single-flight promise has many
   * waiters and they must all be told the same reason.
   */
  #lastRefreshFailure: RefreshFailure | undefined
  #store: KeyValueStore | undefined
  #starting: Promise<void> | undefined
  #recovering: Promise<boolean> | undefined
  /**
   * The restore run in flight, single-flight like `#refreshing`. **`request()` awaits it**, which is
   * what makes a tap during a restore retry the read first and then send, rather than throwing.
   */
  #restoring: Promise<RestoreOutcome> | undefined
  /**
   * Bumped by `endSession`. **A sign-out tapped while a restore is running must win**: without this,
   * the read landing a second later would announce `signed-in` over the resident's own sign-out.
   */
  #restoreGeneration = 0
  #recoveryTimer: ReturnType<typeof setTimeout> | undefined
  #wakeRecovery: (() => void) | undefined

  constructor(
    private readonly transport: ApiTransport,
    private readonly stage: string = STAGE,
    /** D152. Defaults to `navigator.locks` when present; tests inject a fake queue. */
    private readonly locks: LockRequest | undefined = defaultLocks(),
  ) {}

  get state(): SessionState {
    return this.#state
  }

  subscribe(listener: (state: SessionState) => void): () => void {
    this.#listeners.add(listener)
    return () => this.#listeners.delete(listener)
  }

  /** Cancel browser-online backoff without ending the session (React StrictMode-safe). */
  cancelConnectivityRecovery(): void {
    if (this.#recoveryTimer !== undefined) clearTimeout(this.#recoveryTimer)
    this.#recoveryTimer = undefined
    this.#wakeRecovery?.()
    this.#wakeRecovery = undefined
  }

  /**
   * The one way state moves — **and a patch that moves nothing notifies nobody.**
   *
   * `request()` calls `#set({ online: true })` after *every* non-401 response, and without this
   * guard each one reassigned `#state` and woke every listener with a new object carrying
   * identical values. That propagates through `AuthProvider`'s `useSyncExternalStore` → the
   * `sessionApi` memo → fourteen `useSession()` consumers → `DeviceCacheProvider`'s own context
   * memo → ten more, re-arming effects that only early-return guards were holding back. AGENTS.md
   * §4.9 records the measured cost: **2,965 requests from one screen view**, from a resident
   * merely looking at a gate.
   *
   * The equality check removes the amplifier and not the discipline — the per-site brakes
   * (`HomeScreen`'s `warmRequestRef`, `GateDetailScreen`'s, and the three guarded effects) all
   * stay, and §4.9's *hold the function in a ref* is still the rule.
   */
  #set(patch: Partial<SessionState>): void {
    if (!patchMovesState(this.#state, patch)) return
    this.#state = { ...this.#state, ...patch }
    this.#notify()
  }

  /**
   * Replace the whole record — **the only other way `#state` may be assigned.**
   *
   * `adoptSignIn` and `endSession` both reset to `INITIAL` before announcing the new state, and
   * both used to do it with a bare `this.#state = …` that notified nobody. They got away with it
   * only because `INITIAL.state` is `'loading'` and the `#set` that followed always moved it —
   * an accident of today's two patches, not a property of the code. Under the guard above, a
   * future patch that happened to be a no-op relative to the reset would strand every listener on
   * a state the manager had already left. Routing both through here makes the invariant belong to
   * the class instead of to two comments.
   *
   * The reset and the announcement are adjacent and synchronous, so the intermediate `'loading'`
   * is never rendered: `useSyncExternalStore` — the only non-test subscriber — re-reads
   * `manager.state` at render time rather than consuming the value it was notified with.
   */
  #replace(next: SessionState): void {
    if (!statesDiffer(this.#state, next)) return
    this.#state = next
    this.#notify()
  }

  #notify(): void {
    for (const listener of this.#listeners) listener(this.#state)
  }

  async #storage(): Promise<KeyValueStore> {
    this.#store ??= await openDeviceStore()
    return this.#store
  }

  /**
   * Startup contract steps 1–2 and 5's session half: identify the account from the stored
   * binding, then refresh.
   *
   * **A network failure here is not a sign-out.** Losing reception is the specified
   * offline-read-only path, and treating it as a terminal refresh failure would sign a user
   * out for walking into a car park. Only the server saying `401` — revoked, evicted,
   * expired, or replayed — ends the session.
   */
  start(): Promise<void> {
    // Idempotent: React's StrictMode runs mount effects twice in development, and two boot
    // sequences racing would present the same refresh-token family twice — which P2 reads as
    // a replay and answers by killing the session.
    this.#starting ??= this.#doStart()
    return this.#starting
  }

  async #doStart(): Promise<void> {
    const store = await this.#storage()
    if (!store.durable) this.#set({ durableStorage: false })
    await this.#restore('boot')
  }

  /**
   * **The Try again control on the sign-in screen — a fresh restore run, never a page reload.**
   *
   * Reachable only from the state an exhausted run leaves (`signed-out` with
   * `credentialReadFailed`); anywhere else it is a no-op, so a stray call cannot turn a real
   * sign-out back into a read. D309's one-reload-per-process rule and D137's never-self-reload
   * rule both still bind: this re-reads, it does not reload.
   */
  retryRestore(): Promise<void> {
    if (this.#state.state !== 'signed-out' || this.#state.credentialReadFailed !== true) {
      return Promise.resolve()
    }
    return this.#restore('retry')
  }

  /** One restore run, then the boot's session half (step 5) if it found a session. */
  async #restore(trigger: 'boot' | 'retry'): Promise<void> {
    this.#restoring ??= this.#doRestore(trigger).finally(() => {
      this.#restoring = undefined
    })
    const outcome = await this.#restoring
    // The one behavior change D152 makes: a reload with time left on its own cached token
    // never calls `/auth/refresh` at all, so it cannot rotate the refresh family and cannot
    // trip the server's replay detection on the next reload either.
    if (outcome === 'signed-in' && !this.#accessTokenUsable()) await this.#refresh()
  }

  /**
   * **Read the credentials until a read SUCCEEDS, bounded — a rejected read is not an empty store.**
   *
   * The 2026-09-25 production read (`STATE.md` §5): the boot read rejected inside the store's 2 s
   * transaction budget, the old branch here set `signed-out`, and a resident whose row was intact
   * was shown the OTP screen — the next launch read the same row fine. A rejection says nothing
   * about whether the row exists, so it cannot be the answer *signed out*; only a read that
   * succeeds and finds nothing can.
   *
   * So a rejection moves the session to `restoring` and the read runs again on a backoff
   * ({@link CREDENTIAL_READ_RETRY_FIRST_DELAY_MS} doubling to {@link CREDENTIAL_READ_RETRY_MAX_DELAY_MS},
   * no attempt starting past {@link CREDENTIAL_READ_RETRY_BUDGET_MS}). Four rules, each paid for:
   *
   * - **A lost connection is re-read through a FRESH connection** (`reopenDeviceStore()`), never
   *   the handle that just died — `StoreConnectionLostError` is D309's class, and every later call
   *   on that handle fails identically. **And never through a reload**: D309 spends its one reload
   *   per process at resume, where the credentials are known correct, and D137 refuses any other.
   * - **A reopened store that degraded to memory is a FAILED attempt, not an empty one.** A memory
   *   store answers every `get` with nothing, so reading it as *no row* is this defect again
   *   through the reopen path — `storeRecovery.ts`'s `durableOrThrow` reasoning, one caller over.
   *   The boot's own store is exempt: a launch that could not open IndexedDB at all is D306's
   *   `store-open-degraded` case and lands signed out exactly as before.
   * - **The bound is R1-3's**: a store that rejects forever ends on the sign-in screen with
   *   `credentialReadFailed` (the storage line and Try again), never on a spinner.
   * - **It reports** — `credential-read-failed` on the first rejection (so a run the OS kills
   *   mid-way still leaves its trace) and again when the run ends, `recovered` or `exhausted`.
   *   Before this the only trace was a `console.warn` nothing on a store build reads.
   */
  async #doRestore(trigger: 'boot' | 'retry'): Promise<RestoreOutcome> {
    const started = Date.now()
    const generation = this.#restoreGeneration
    const superseded = (): boolean => generation !== this.#restoreGeneration
    const bootStoreDurable = (await this.#storage()).durable
    if (trigger === 'retry') this.#set({ state: 'restoring', credentialReadFailed: undefined })
    let delay = CREDENTIAL_READ_RETRY_FIRST_DELAY_MS
    let reopen = false
    let lastErrorName = 'none'

    for (let attempt = 1; ; attempt += 1) {
      let credentials: StoredCredentials | undefined
      let store: KeyValueStore | undefined
      try {
        if (reopen) {
          this.#store = undefined
          store = await reopenDeviceStore()
          this.#store = store
          reopen = false
        } else {
          store = await this.#storage()
        }
        if (bootStoreDurable && !store.durable) throw new StoreDegradedToMemoryError()
        credentials = await readCredentials(store, this.stage)
      } catch (error) {
        if (superseded()) return 'absent'
        lastErrorName = errorNameOf(error)
        // D339 — the container's first read after the upgrade carries the IndexedDB row into the
        // keystore, and a legacy store that fell to memory cannot answer *no row*; it is re-read
        // through a fresh connection like the degrade it is.
        if (
          error instanceof StoreConnectionLostError ||
          error instanceof StoreDegradedToMemoryError ||
          error instanceof LegacyCredentialsUnreadableError
        ) {
          reopen = true
        }
        const elapsedMs = Date.now() - started
        const exhausted = elapsedMs + delay >= CREDENTIAL_READ_RETRY_BUDGET_MS
        // `retrying` on EVERY run's first rejection — including one whose first attempt already ran
        // the budget out — because it is the class's count and `recovered`'s denominator; a run that
        // skipped it would vanish from both exactly on the slowest-failing devices.
        if (attempt === 1) {
          this.#report({ outcome: 'retrying', errorName: lastErrorName, elapsedMs, attempt, trigger })
        }
        if (exhausted) {
          this.#report({ outcome: 'exhausted', errorName: lastErrorName, elapsedMs, attempt, trigger })
        }
        if (exhausted) {
          this.#set({ state: 'signed-out', credentialReadFailed: true })
          return 'failed'
        }
        this.#set({ state: 'restoring' })
        await sleep(delay)
        if (superseded()) return 'absent'
        delay = Math.min(delay * 2, CREDENTIAL_READ_RETRY_MAX_DELAY_MS)
        continue
      }
      if (superseded()) return 'absent'

      if (attempt > 1) {
        this.#report({
          outcome: 'recovered',
          errorName: lastErrorName,
          elapsedMs: Date.now() - started,
          attempt,
          trigger,
        })
      }
      if (credentials === undefined) {
        this.#set({ state: 'signed-out', credentialReadFailed: undefined })
        return 'absent'
      }
      await this.#adoptStoredCredentials(store, credentials)
      return 'signed-in'
    }
  }

  /** Fire-and-forget, and guarded: a diagnostic must never be what fails a restore. */
  #report(event: Parameters<typeof reportCredentialReadFailed>[0]): void {
    try {
      void reportCredentialReadFailed(event)
    } catch (error) {
      console.warn('credential-read-report-failed', error)
    }
  }

  /** Startup contract steps 2–3's session half: the account is identified, render from cache. */
  async #adoptStoredCredentials(store: KeyValueStore, credentials: StoredCredentials): Promise<void> {
    this.#credentials = credentials
    // D152 — adopt a still-usable per-tab token before deciding whether this load needs the
    // network at all. `sessionStorage` is per-tab, so a fresh tab or one past its own token's
    // expiry finds nothing here and falls through to the refresh below exactly as before.
    this.#adoptStoredAccessToken()

    // Render from cache before the network answers: the profile is what `pendingName` reads,
    // and holding the app on a spinner until a refresh completes would defeat the point of
    // an offline-capable launch. The profile is a cache, not a gate (`rememberProfile`'s rule),
    // so a read failure costs the pre-network name and nothing else.
    const profile = await readProfile(store, this.stage, credentials.userId).catch((error: unknown) => {
      console.warn('profile-cache-read-failed', error)
      return undefined
    })
    this.#set({
      state: 'signed-in',
      credentialReadFailed: undefined,
      userId: credentials.userId,
      sessionId: credentials.sessionId,
      ...(profile?.name === undefined ? {} : { name: profile.name }),
      ...(profile?.locale === undefined ? {} : { locale: profile.locale }),
    })
    this.#purgeOthersAtBoot(store, credentials.userId)
  }

  /**
   * **The backstop for a sign-in whose account-switch purge could not run** (see `adoptSignIn`).
   *
   * Nothing on the device can record that the purge is owed: the store that would hold the note is
   * the one that just failed, and WebKit serves `localStorage` from the same dead Networking process.
   * So every restored launch asks the store — **one `keys('snapshots')` read**, which
   * `purgeOtherAccounts`' ordering makes sufficient — and runs the purge only when it finds another
   * account's row. Unawaited and best-effort: the boot read that already succeeded is what this
   * launch's session rests on. **It stops the moment this account is no longer the session's**, so
   * a sign-out and another sign-in during a slow run cannot make the new account's rows "other".
   */
  #purgeOthersAtBoot(store: KeyValueStore, userId: string): void {
    const stillCurrent = (): boolean => this.#credentials?.userId === userId
    void (async () => {
      if (!(await hasOtherAccountRows(store, this.stage, userId))) return
      if (stillCurrent()) await purgeOtherAccounts(store, this.stage, userId, stillCurrent)
    })().catch((error: unknown) => {
      console.warn('boot-purge-failed', error)
    })
  }

  /** D152. Reads this tab's own cached token; leaves `#accessToken` untouched if absent or malformed. */
  #adoptStoredAccessToken(): void {
    const stored = readAccessToken(this.stage)
    if (stored === undefined) return
    this.#accessToken = stored.token
    this.#accessTokenExpiresAt = stored.expiresAt
  }

  /**
   * Adopt the session `POST /auth/otp/verify` just issued.
   *
   * Order matters: **persist before announcing**. A crash between the two leaves a device
   * that can restore silently; the reverse leaves a signed-in UI whose next launch is an
   * unexplained OTP.
   */
  async adoptSignIn(input: VerifiedSignIn, accessToken: string, expiresInSeconds: number): Promise<void> {
    let store = await this.#storage()
    // `writeCredentials` stays fail-hard — **that one is the session.** The profile beside it
    // is a cache the next refresh rebuilds, and letting it throw froze `verifyCode` on the
    // code screen in exactly the way a failed `rememberProfile` froze the name screen.
    //
    // **D304 — the ladder runs here rather than in `AuthProvider`'s catch, and the reason is this
    // class's own `#store` cache.** Rung 1 deletes the database and rung 2 replaces the handle, so
    // whichever store survives has to be adopted by the field above or every later read in this
    // session goes to a handle pointing at a database that no longer exists. `AuthProvider` cannot
    // reach `#store`; this is the only seam that can, which is why the handoff's "in `adoptSignIn`'s
    // catch" lands one layer lower than it reads. Classification of the *rendered* error stays up
    // there, where the copy is.
    //
    // **Still fail-closed.** A ladder that ran out of rungs rethrows the original failure, so
    // D128's posture is untouched: no degraded memory-store session, explicitly declined.
    try {
      await writeCredentials(store, this.stage, input.credentials)
    } catch (failure) {
      const recovered = await recoverCredentialWrite({
        failure,
        store,
        write: (fresh) => writeCredentials(fresh, this.stage, input.credentials),
      })
      if (recovered.rung === 'none') throw failure
      store = recovered.store
      this.#store = recovered.store
    }
    // **2026-10-16 — once the credentials are written, nothing after them may fail the sign-in.**
    // The keystore write (D339) succeeded and the account-switch purge's IndexedDB read then threw
    // on a dead WebKit storage connection (D309's mechanism); the throw reached `AuthProvider` as
    // *reinstall the app* over a session that was already saved, so the resident typed a second code
    // and paid a second SMS, and a force-quit later signed them in with none. D128's fail-closed
    // posture is about the credential write and is untouched — **that** write still throws above.
    //
    // **So everything after it runs as ONE guarded block**, {@link #afterCredentialWrite}, rather
    // than a try per step: a step added there later cannot reach the code screen however it fails,
    // which a per-step guard only promises for the steps that already have one.
    //
    // **No reload here**: D309's one reload is spent at resume and D137 refuses any other — a second
    // site is a decision, not a fix. A lost connection is re-opened instead, as `#doRestore` does:
    // on iOS 26.5 and above that alone heals it, and below it the reopen fails and the dead handle
    // stays, with `durableStorage` saying so. 🔸 The reopen is awaited, so the worst case adds
    // `STORE_OPEN_TIMEOUT_MS` to a spinner the resident is already watching — on a path that used to
    // cost them an SMS.
    let durableStorage = store.durable
    try {
      await this.#afterCredentialWrite(store, input)
    } catch (error) {
      console.warn('sign-in-purge-failed', error)
      try {
        void reportSignInPurgeFailed({
          errorName: errorNameOf(error),
          signal: error instanceof StoreConnectionLostError ? error.signal : 'none',
        })
      } catch (reportError) {
        console.warn('sign-in-purge-report-failed', reportError)
      }
      if (error instanceof StoreConnectionLostError) durableStorage = await this.#reopenAfterLostConnection()
    }
    writeResumeHint(this.stage, input.credentials.sessionId)

    this.#credentials = input.credentials
    this.#setAccessToken(accessToken, expiresInSeconds)
    this.#replace({ ...INITIAL, durableStorage })
    this.#set({
      state: 'signed-in',
      userId: input.credentials.userId,
      sessionId: input.credentials.sessionId,
      ...(input.profile.name === undefined ? {} : { name: input.profile.name }),
      locale: input.profile.locale,
      online: true,
    })
  }

  /**
   * Everything `adoptSignIn` does after the credential write, and **nothing in here may decide
   * whether the sign-in succeeded** — its caller reports a throw and completes regardless. The
   * profile and the marker keep their own guards so one failing does not skip the purge.
   *
   * **What skipping the purge costs, and why that is not a privacy hole.** The rule it enforces is
   * startup step 2's *never show another account's snapshot*, and showing is already keyed: the
   * snapshot, the profile and the outbox are each read by the signed-in `userId`, so a previous
   * account's rows cannot render or upload under this one. What survives is those rows at rest
   * until {@link #purgeOthersAtBoot} removes them on the next launch with a live connection.
   */
  async #afterCredentialWrite(store: KeyValueStore, input: VerifiedSignIn): Promise<void> {
    try {
      await writeProfile(store, this.stage, input.profile)
    } catch (error) {
      console.warn('profile-cache-write-failed', error)
    }
    // A different account on this device: the previous one's gates go now, not on its next
    // launch (there may not be one).
    await purgeOtherAccounts(store, this.stage, input.credentials.userId)
    // D152 — a brand-new sign-in has no refresh attempt in flight; a marker left over from a
    // previous account's interrupted refresh would otherwise sit there meaninglessly.
    await clearRefreshMarker(store, this.stage).catch(() => {
      /* best-effort, see `#doRefreshLocked` */
    })
  }

  /**
   * A fresh connection for the rest of this process, adopted only if it is durable — a memory store
   * would make every later write "succeed" into nothing. Returns what `durableStorage` should say.
   */
  async #reopenAfterLostConnection(): Promise<boolean> {
    try {
      const fresh = await reopenDeviceStore()
      if (!fresh.durable) return false
      this.#store = fresh
      return true
    } catch (error) {
      console.warn('sign-in-reopen-failed', error)
      return false
    }
  }

  /** The id to offer as `resumeSessionId` on the next verify — see `credentials.ts`. */
  resumeSessionId(): string | undefined {
    return readResumeHint(this.stage)
  }

  /**
   * Cache what `PATCH /users/me` returned, so a reload does not re-prompt for the name.
   *
   * **The write is best-effort, and deliberately so.** The name is already stored on the
   * server by the time this runs; this record only saves the next launch a re-read. Letting
   * its failure propagate turned a broken IndexedDB into a locked door — the name step's own
   * caller never cleared `busy`, so the button stayed disabled with the name already
   * accepted. Fixed here rather than at the call site, so the drawer's rename
   * (`screens/shared/api.ts` → `patchProfile`) is covered by the same change.
   *
   * `durableStorage` stays untouched: that flag means *the store gave us nothing durable*,
   * and reporting a working store as broken because one write failed would be a worse lie
   * than the one being fixed.
   */
  async rememberProfile(profile: CachedProfile): Promise<void> {
    try {
      await writeProfile(await this.#storage(), this.stage, profile)
    } catch (error) {
      console.warn('profile-cache-write-failed', error)
    }
    this.#set({
      ...(profile.name === undefined ? {} : { name: profile.name }),
      locale: profile.locale,
    })
  }

  #setAccessToken(token: string, expiresInSeconds: number): void {
    this.#accessToken = token
    this.#accessTokenExpiresAt = Date.now() + Math.max(0, expiresInSeconds - ACCESS_TOKEN_SKEW_SECONDS) * 1000
    // D152 — best-effort by construction (`writeAccessToken` never throws): a failed write
    // costs this tab one avoidable refresh on its next reload, never a session.
    writeAccessToken(this.stage, { token, expiresAt: this.#accessTokenExpiresAt })
  }

  #accessTokenUsable(): boolean {
    return this.#accessToken !== undefined && Date.now() < this.#accessTokenExpiresAt
  }

  /**
   * An authenticated call. **The only one in the app.**
   *
   * Attaches the bearer token and the session binding, refreshes ahead of expiry, and
   * retries a `401` exactly once behind a fresh token. A `401` that survives that retry is
   * the server refusing a session this client thinks it has — revoked, LRU-evicted, or
   * expired — and the answer is teardown, never a third attempt.
   *
   * **A failed refresh throws the reason, not a category.** A 5xx on `/auth/refresh` is not
   * the phone being offline, and a caller that renders both as "check your reception" sends
   * the user to look at their signal bars while the server is down.
   */
  async request(request: ApiRequest): Promise<ApiResponse> {
    // **A call made while the credentials are being re-read waits for the read, then goes.** The
    // restore is bounded ({@link CREDENTIAL_READ_RETRY_BUDGET_MS}), so this wait is too. A run that
    // found the session proceeds exactly as a signed-in call; one that exhausted its budget throws
    // `SessionUnavailableError` — the `NetworkError` subclass every caller already degrades on
    // (D139) — rather than a bare `Error` a screen would rethrow.
    if (this.#credentials === undefined && this.#restoring !== undefined) {
      const outcome = await this.#restoring
      // Every outcome that found no session, not only the exhausted one: a later read that finds no
      // row, or a sign-out that superseded the run, leaves a caller that was already waiting here,
      // and the bare `Error` below is one no screen catches.
      if (outcome === 'failed') throw new SessionUnavailableError('credential-read-failed')
      if (this.#credentials === undefined) throw new SessionUnavailableError('no-session')
    }
    if (this.#credentials === undefined) throw new Error('request() called with no session')

    if (!this.#accessTokenUsable() && !(await this.#refresh())) {
      throw this.#refreshFailure('no-access-token')
    }

    const response = await this.#send(request)
    if (response.status !== 401) {
      this.#set({ online: true })
      return response
    }

    // One retry, behind a refresh that is itself allowed to end the session.
    if (!(await this.#refresh())) throw this.#refreshFailure('refresh-failed')
    const retried = await this.#send(request)
    if (retried.status === 401) {
      await this.endSession('revoked')
      return retried
    }
    this.#set({ online: true })
    return retried
  }

  /** The error a failed refresh deserves. `NetworkError` is reserved for a real one. */
  #refreshFailure(cause: string): NetworkError {
    return this.#lastRefreshFailure === 'offline'
      ? new NetworkError(cause)
      : new SessionUnavailableError(cause)
  }

  /**
   * The credentials every authenticated call carries.
   *
   * **Extracted so there is still exactly one place that attaches them**, which is the substance
   * of the one-authenticated-door rule — not the number of `transport` call sites. D308's
   * acknowledgement is the second reader and is deliberately not a third construction.
   */
  #authHeaders(binding: string, accessToken: string): Record<string, string> {
    return {
      authorization: `Bearer ${accessToken}`,
      [SESSION_BINDING_HEADER]: binding,
      // Rung 2 of `review/05-client-proof-ladder.md`, and it belongs *here* for the same
      // reason the binding does: this is the one authenticated door, so a header set here
      // is on every authenticated call a route added later makes, with nothing to
      // remember. Spread last so a caller cannot shadow it — the point of the header is
      // that the client states its own build, not that a call site decides what to claim.
      [CLIENT_BUILD_HEADER]: BUILD.commit,
    }
  }

  async #send(request: ApiRequest): Promise<ApiResponse> {
    const credentials = this.#credentials
    if (credentials === undefined || this.#accessToken === undefined) {
      throw new Error('send() called with no session')
    }
    try {
      return await this.transport({
        ...request,
        headers: {
          ...request.headers,
          ...this.#authHeaders(credentials.binding, this.#accessToken),
        },
      })
    } catch (error) {
      if (error instanceof NetworkError) this.#set({ online: false })
      throw error
    }
  }

  /**
   * **D308 — tell the server the rotation landed, once it actually has.**
   *
   * The server keeps the rotation's result until it hears this, and replays it to a device that
   * comes back holding the superseded family id — which is what stops one lost refresh reply
   * destroying a session (`session-eviction-handoff.md` §3.2). This call is what makes that
   * credential's life **seconds**: without it the generation closes only on the device's next
   * refresh, days for an idle one, so every live session would carry a token at rest instead of
   * only the broken ones.
   *
   * **It is called after the durable write and never before, and that ordering is the whole
   * meaning of it.** An ack sent alongside the write — or on a write that then failed — marks a
   * generation acknowledged that this device never stored, and the server then reads its honest
   * retry as a second holder and revokes. That is today's defect with an extra step in front of
   * it, and it is silent.
   *
   * **Fire-and-forget, and deliberately not through `request()` or `#send`.** Nothing waits on it
   * and nothing reads its answer: a lost, refused or late ack costs nothing, because a successful
   * presentation of the current family id is itself an acknowledgement. `#send` would latch
   * `online: false` on a network failure (D264), which here would flip the app to read-only
   * **immediately after a refresh proved the network works**; `request()` would refresh ahead of
   * expiry and retry a `401`, which is a lot of machinery to spend on a message whose failure
   * mode is "nothing happens". It carries the same headers as every other authenticated call,
   * from the same builder.
   *
   * **The guard is the token's REAL remaining life, never `#accessTokenUsable()`, and the
   * difference is the whole population this call has to reach.** That predicate subtracts
   * `ACCESS_TOKEN_SKEW_SECONDS`, so a replayed rotation served with a minute or less left — the
   * device whose row is *actually* holding a stored credential, which is the only device the ack
   * matters for — was skipped while carrying a token the server would have accepted. What cannot
   * be acked is a token with nothing left: `expiresIn: 0` is what a replay minted long ago answers
   * on purpose, and acking with it would spend a `401` to learn what the response already said.
   * That one case closes on the next rotation, which acks a generation superseding this one.
   */
  #acknowledgeRotation(credentials: StoredCredentials, expiresInSeconds: number): void {
    if (expiresInSeconds <= 0 || this.#accessToken === undefined) return
    const accessToken = this.#accessToken
    // **Both failure shapes, because a transport may throw synchronously as well as reject.**
    // `.catch()` alone covers only the second, and the first would escape into
    // `#doRefreshLocked` and turn a successful refresh into a thrown one — a caller signed out
    // by the message that was supposed to keep it signed in.
    void (async () => {
      try {
        await this.transport({
          method: ROUTES.sessionRefreshAck.method,
          path: buildPath(ROUTES.sessionRefreshAck),
          headers: this.#authHeaders(credentials.binding, accessToken),
          body: { refreshFamilyId: credentials.refreshFamilyId },
        })
      } catch {
        // Silent by design. There is no recovery, no retry and nothing to report: the implicit
        // acknowledgement closes this generation on the next successful refresh either way.
      }
    })()
  }

  /**
   * `POST /auth/refresh` — the boot-time silent refresh and the `401` recovery, single-flight
   * so a burst of parallel requests produces one rotation rather than a self-inflicted
   * token-family reuse (which P2 answers by killing the session outright).
   */
  #refresh(): Promise<boolean> {
    this.#refreshing ??= this.#doRefresh().finally(() => {
      this.#refreshing = undefined
    })
    return this.#refreshing
  }

  /**
   * Browser `online` recovery. Duplicate events share this promise, and false-positive online
   * events get only the bounded 0/1/3-second sequence before the app remains read-only.
   */
  recoverConnectivity(): Promise<boolean> {
    if (this.#state.state !== 'signed-in' || this.#state.online || this.#credentials === undefined) {
      return Promise.resolve(this.#state.online)
    }
    this.#recovering ??= this.#doRecoverConnectivity().finally(() => {
      this.#recovering = undefined
    })
    return this.#recovering
  }

  async #doRecoverConnectivity(): Promise<boolean> {
    for (const delayMs of CONNECTIVITY_RETRY_DELAYS_MS) {
      if (this.#credentials === undefined || this.#state.state !== 'signed-in') return false
      if (delayMs > 0) await this.#waitForRecovery(delayMs)
      if (this.#credentials === undefined || this.#state.state !== 'signed-in') return false
      if (await this.#refresh()) return true
    }
    return false
  }

  #waitForRecovery(delayMs: number): Promise<void> {
    return new Promise((resolve) => {
      const done = () => {
        this.#recoveryTimer = undefined
        this.#wakeRecovery = undefined
        resolve()
      }
      this.#wakeRecovery = done
      this.#recoveryTimer = setTimeout(done, delayMs)
    })
  }

  /**
   * D152 — wraps the actual refresh in a best-effort Web Lock, named per stage. Within one tab
   * `#refresh()`'s single-flight already means only one of these is ever in progress, so the
   * lock only ever contends **across** tabs/documents of the same origin — which is exactly the
   * case a reload's fresh document is, racing whatever the previous document had in flight.
   * Feature-detected: `this.locks` is `undefined` wherever `navigator.locks` is absent, and the
   * refresh proceeds unlocked exactly as it did before D152.
   */
  #doRefresh(): Promise<boolean> {
    return this.locks === undefined
      ? this.#doRefreshLocked()
      : this.locks(refreshLockName(this.stage), () => this.#doRefreshLocked())
  }

  async #doRefreshLocked(): Promise<boolean> {
    this.#lastRefreshFailure = undefined
    // Captured once, synchronously, and never re-read from `this.#credentials` below — a
    // concurrent `endSession()` (a user tapping sign-out while this refresh awaits the lock or
    // the network) can clear that field mid-flight, and reading it again after an `await` would
    // crash rather than degrade. Everything from here on works off this local.
    const originalCredentials = this.#credentials
    if (originalCredentials === undefined) {
      this.#lastRefreshFailure = 'ended'
      return false
    }
    const store = await this.#storage()

    // D152 — re-read before presenting anything. Whoever held the lock before us (another tab,
    // or this tab's own previous attempt if the lock is unavailable) may have already rotated
    // the family id durably; using our own possibly-stale copy is exactly the replay the server
    // is right to refuse. This is the one thing that can actually *prevent* an otherwise-honest
    // refusal, as opposed to merely explaining one after the fact.
    const reread = await readCredentials(store, this.stage).catch(() => undefined)
    const credentials =
      reread !== undefined && reread.userId === originalCredentials.userId ? reread : originalCredentials

    // D152 — an honest, non-suppressing signal only. A marker naming the exact id we are about
    // to present means a previous attempt with this id was interrupted before it could record
    // an outcome — most likely a reload landing mid-flight. It changes nothing about what
    // happens next: only a `401` ends a session, and this may still be a genuine one (a real
    // revoke, an LRU eviction). It only lets that refusal be logged as probably self-inflicted
    // rather than as an unexplained revoke.
    const marker = await readRefreshMarker(store, this.stage).catch(() => undefined)
    const racedInterruptedAttempt = marker?.familyId === credentials.refreshFamilyId

    try {
      await writeRefreshMarker(store, this.stage, credentials.refreshFamilyId)
    } catch (error) {
      // Best-effort: the marker is a diagnostic aid, not the credential. Losing it costs this
      // one refusal its honest label, never a session.
      console.warn('refresh-marker-write-failed', error)
    }

    let response: ApiResponse
    try {
      response = await this.transport({
        method: ROUTES.authRefresh.method,
        path: buildPath(ROUTES.authRefresh),
        body: {
          refreshToken: credentials.refreshToken,
          binding: credentials.binding,
          refreshFamilyId: credentials.refreshFamilyId,
        },
      })
    } catch (error) {
      // Unreachable, not refused. Keep the credentials and run read-only.
      if (error instanceof NetworkError) {
        this.#lastRefreshFailure = 'offline'
        this.#set({ online: false })
        return false
      }
      throw error
    }

    if (response.status === 401) {
      // Terminal: revoked, LRU-evicted, expired, or a replayed token family. All four mean
      // this device is no longer a session, and none of them are retryable. D152 changes
      // nothing here except the log line: the session still ends.
      if (racedInterruptedAttempt) {
        console.warn('refresh-replay-after-interrupted-attempt', { familyId: credentials.refreshFamilyId })
      }
      // D306 — the same bit, to somebody. The `console.warn` above reaches nobody on a store
      // build, and the server cannot compute it: whether the id it just refused was one this
      // device had already presented and never heard back about is the split between an
      // unacknowledged rotation and a genuine replay. Reported before the purge below, on the
      // sink's own transport, and never awaited — a diagnostic must not delay the teardown.
      void reportRefreshRefused({ interruptedAttempt: racedInterruptedAttempt })
      this.#lastRefreshFailure = 'ended'
      await this.endSession('revoked')
      return false
    }
    if (response.status !== 200) {
      // 5xx or a transport oddity — transient by assumption. The credentials survive and the
      // next request tries again; what must not happen is a server hiccup signing people out.
      // It is **not** offline, though: `online: false` is the app's read-only mode, while the
      // failure reason is what decides the words a caller is allowed to put on screen.
      this.#lastRefreshFailure = 'server'
      this.#set({ online: false })
      return false
    }

    const accessToken = stringField(response.body, 'accessToken')
    const refreshFamilyId = stringField(response.body, 'refreshFamilyId')
    const expiresIn = numberField(response.body, 'expiresIn') ?? 0
    if (accessToken === undefined || refreshFamilyId === undefined) {
      this.#lastRefreshFailure = 'server'
      this.#set({ online: false })
      return false
    }

    const rotated: StoredCredentials = {
      ...credentials,
      // Cognito may or may not rotate the refresh token itself; the family id always rotates.
      refreshToken: stringField(response.body, 'refreshToken') ?? credentials.refreshToken,
      refreshFamilyId,
    }
    // **Persist the rotation before using the token it came with.** If the write is lost, the
    // next refresh presents a stale family id and P2 correctly reads that as a replay and
    // kills the session — so the durable write is what has to happen first.
    try {
      await writeCredentials(store, this.stage, rotated)
    } catch (failure) {
      // **D309 — the resume rung, and it goes BEFORE the sign-out because that is where it is
      // free.** This is the silent sign-out: a refresh that succeeded and whose credential write
      // was refused, which on iOS below 26.5 means the WKWebView's Networking process was killed
      // and every IndexedDB connection with it. The database is fine and the credentials on disk
      // are still correct — only the *connection* is dead, and only a page reload replaces it. At
      // this instant that reload costs a splash screen and no D65/D88 cap slot; one line down, past
      // `endSession('storage-lost')`, the same reload costs a fresh SMS and one of five slots per
      // rolling 5 h. Bounded to one per app process by a native marker, reported either way, and
      // D137's *the app never reloads itself* is amended for this one case and no other — see
      // `storeRecovery.ts`.
      //
      // `true` means the document is being replaced. Returning without touching the session is
      // deliberate: the credentials on disk are the ones the next launch must read, and ending the
      // session here would destroy them on the way out.
      if (await recoverLostStoreConnection(failure)) return false
      // Cognito has already rotated the token. Using it before the replacement token/family is
      // durable would strand this device on reload, so fail closed to a fresh OTP instead.
      this.#lastRefreshFailure = 'ended'
      await this.endSession('storage-lost')
      return false
    }
    // D152 — cleared in the same commit as the rotation it names. A best-effort failure here
    // leaves a stale marker for the *next* refresh to find, which only ever affects a log line.
    await clearRefreshMarker(store, this.stage).catch((error: unknown) => {
      console.warn('refresh-marker-clear-failed', error)
    })
    this.#credentials = rotated
    this.#setAccessToken(accessToken, expiresIn)
    this.#set({ state: 'signed-in', online: true })
    // **D308 — here, and not one line earlier.** Everything above this point is what makes the
    // acknowledgement true: the rotation is on disk, it is the manager's live credential, and the
    // token that authenticates the call is the one this rotation returned.
    this.#acknowledgeRotation(rotated, expiresIn)
    await this.#adoptRefreshedProfile(field(response.body, 'user'))
    return true
  }

  /**
   * The profile `POST /auth/refresh` returns — **the server as the boot authority for D42's
   * name**, which the local cache alone could not be.
   *
   * *"Present" means adopt; "absent" means leave the cached value alone.* A self-name is
   * set-once-then-editable and is never removed server-side (D133(f)), so an absent `name` is
   * either not-yet-captured or a response-shape regression — and mirroring absence would send
   * every signed-in user back to the name gate the first time a field got dropped. The write
   * is best-effort for the same reason `rememberProfile`'s is: a cache is not a gate.
   */
  async #adoptRefreshedProfile(user: unknown): Promise<void> {
    const userId = stringField(user, 'userId')
    if (userId === undefined || userId !== this.#credentials?.userId) return

    const name = stringField(user, 'name') ?? this.#state.name
    const reported = stringField(user, 'locale')
    const locale: Locale = isLocale(reported) ? reported : (this.#state.locale ?? 'he')
    const profile: CachedProfile = { userId, ...(name === undefined ? {} : { name }), locale }

    try {
      await writeProfile(await this.#storage(), this.stage, profile)
    } catch (error) {
      console.warn('profile-cache-write-failed', error)
    }
    this.#set({ ...(name === undefined ? {} : { name }), locale })
  }

  /**
   * Sign out — *"revoking the current session is sign-out"* (§9).
   *
   * The local teardown happens **whether or not the server call succeeds**. A user tapping
   * sign-out on a shared phone with no reception must not be left holding a live binding and
   * another person's gate list; the server-side record is reaped by its TTL in the worst case.
   */
  async signOut(): Promise<void> {
    const credentials = this.#credentials
    if (credentials !== undefined && this.#accessTokenUsable()) {
      try {
        await this.#send({
          method: ROUTES.sessionRevoke.method,
          path: buildPath(ROUTES.sessionRevoke, { sessionId: credentials.sessionId }),
        })
      } catch {
        /* offline sign-out is still a sign-out */
      }
    }
    await this.endSession('sign-out')
  }

  /**
   * Every path that ends a session lands here: sign-out, a terminal `401` (revoke, LRU
   * eviction, administrative removal, token replay), and storage that vanished. One
   * teardown, so none of them can be the one that forgets the snapshot.
   */
  async endSession(reason: SessionEndReason): Promise<void> {
    this.#restoreGeneration += 1
    const userId = this.#credentials?.userId ?? this.#state.userId
    this.#accessToken = undefined
    this.#accessTokenExpiresAt = 0
    this.#credentials = undefined
    this.cancelConnectivityRecovery()
    clearResumeHint(this.stage)
    // D152 — every path that ends a session clears this tab's cached token too, same as it
    // always cleared the in-memory field.
    clearAccessToken(this.stage)

    const store = await this.#storage()
    let durableStorage = store.durable
    if (userId !== undefined) {
      try {
        await purgeAccount(store, this.stage, userId)
      } catch {
        durableStorage = false
        try {
          await store.destroy()
        } catch {
          // Volatile credentials are already gone. A failed durable destroy is surfaced by the
          // non-durable state and must never keep the UI signed in.
        }
      }
    } else {
      // **No account is known — a sign-out during `restoring`, before any read landed — and the
      // row is still on disk.** Clearing it needs no account: the key is the stage. Without this
      // the in-memory sign-out wins and the next launch reads the row and signs the resident back
      // in. A clear that fails leaves the pending-clear flag (`clearCredentials`), which the next
      // read finishes; nothing is destroyed here, because a store that cannot be read is D309's lost
      // connection as often as it is damage.
      await clearCredentials(store, this.stage).catch(() => undefined)
    }
    if (reason === 'storage-lost') {
      durableStorage = false
      try {
        await store.destroy()
      } catch {
        // Memory state is already terminal; the next launch must treat storage as untrusted.
      }
    }

    this.#replace({ ...INITIAL, durableStorage })
    this.#set({ state: 'signed-out', endedReason: reason })
  }
}

/**
 * Read the `POST /auth/otp/verify` body into the two records this device keeps.
 *
 * Returns `undefined` rather than throwing if a field is missing: an unusable sign-in
 * response must leave the user on the OTP screen with an error, not half-signed-in.
 */
export function readVerifiedSignIn(
  body: unknown,
): { readonly signIn: VerifiedSignIn; readonly accessToken: string; readonly expiresIn: number } | undefined {
  const tokens = field(body, 'tokens')
  const session = field(body, 'session')
  const user = field(body, 'user')

  const accessToken = stringField(tokens, 'accessToken')
  const refreshToken = stringField(tokens, 'refreshToken')
  const sessionId = stringField(session, 'sessionId')
  const binding = stringField(session, 'binding')
  const refreshFamilyId = stringField(session, 'refreshFamilyId')
  const userId = stringField(user, 'userId')
  if (
    accessToken === undefined ||
    refreshToken === undefined ||
    sessionId === undefined ||
    binding === undefined ||
    refreshFamilyId === undefined ||
    userId === undefined
  ) {
    return undefined
  }

  const name = stringField(user, 'name')
  const locale = stringField(user, 'locale')
  return {
    signIn: {
      credentials: { userId, sessionId, binding, refreshToken, refreshFamilyId },
      profile: {
        userId,
        ...(name === undefined ? {} : { name }),
        locale: isLocale(locale) ? locale : 'he',
      },
    },
    accessToken,
    expiresIn: numberField(tokens, 'expiresIn') ?? 0,
  }
}
